/**
 * /staff — barber clock-in screen, installed as its own iPad app.
 *
 * Distinct from the customer /kiosk flow: this is the staff-facing surface
 * where subcontractors mark themselves on and off the floor. Clock-in order
 * sets the rotation, so the header pills show position badges — a barber
 * can see they're third in line before they ask.
 *
 * PIN never leaves as a hash: the kiosk posts a PIN to a callable and gets
 * a boolean. /barbers/{slug} is unreadable from any client (firestore.rules).
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFns } from '../../lib/firebase';
import { useKioskGround, KIOSK_NAVY } from '../../components/kiosk/useKioskGround';
import { useShopConfig } from '../../hooks/useShopConfig';
import { useClockedIn } from '../../hooks/useShopState';

type Mode = 'idle' | 'pin' | 'setup' | 'done' | 'reset-pick' | 'reset-auth' | 'reset-confirm' | 'reset-done';

export default function StaffClockIn() {
  useKioskGround(KIOSK_NAVY);
  const { barbers, config } = useShopConfig();
  const { barbers: onFloor, inOrder } = useClockedIn();
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doneAction, setDoneAction] = useState<'in' | 'out'>('in');

  /**
   * Owner reset. ownerAuthed is set only by a successful clock-in or
   * clock-out as the owner, and lives in memory for this mount — nothing is
   * persisted, so a walk-up after the iPad has been sitting idle shows
   * nothing. It gates the entry point's VISIBILITY only; the actual gate is
   * the owner's PIN, which resetBarberPin verifies server-side. Hiding a
   * button is not access control.
   */
  const [ownerAuthed, setOwnerAuthed] = useState(false);
  const [resetTarget, setResetTarget] = useState<string | null>(null);

  const pinLength = config.pin_length;
  const isOn = (slug: string) => Boolean(onFloor[slug]);

  /**
   * Setup mode needs BOTH fields complete and matching. The old gate only
   * checked `pin`, so Confirm was enabled with an empty confirmPin — the
   * server's zod regex rejected the empty string and the failure surfaced
   * as a generic "Something went wrong."
   */
  const canSubmit =
    !busy &&
    pin.length === pinLength &&
    (mode !== 'setup' || (confirmPin.length === pinLength && pin === confirmPin));

  const reset = () => {
    setResetTarget(null);
    setSelected(null);
    setMode('idle');
    setPin('');
    setConfirmPin('');
    setError(null);
    setInfo(null);
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === 'setup') {
        const setFn = httpsCallable(getFns(), 'setBarberPin');
        await setFn({ barberSlug: selected, pin, confirmPin });

        // Deliberately do NOT chain into clock-in here. The pinHash write
        // may not be readable yet when clockIn calls verifyBarberPin, which
        // would reject a PIN the barber just set correctly. Bounce back to
        // entry instead — one extra tap, no phantom failure.
        setMode('pin');
        setPin('');
        setConfirmPin('');
        setError(null);
        setInfo('PIN saved. Enter it again to clock in.');
        setBusy(false);
        return;
      }

      const clockingOut = isOn(selected);
      const fn = httpsCallable(getFns(), clockingOut ? 'clockOut' : 'clockIn');
      await fn({ barberSlug: selected, pin });
      // Same callable, same payload — only what is on screen afterwards
      // is new. A grid that silently reappeared gave a barber no way to
      // tell a successful clock-in from a tap that missed the button.
      if (selected === owner?.slug) setOwnerAuthed(true);
      setDoneAction(clockingOut ? 'out' : 'in');
      setPin('');
      setConfirmPin('');
      setMode('done');
    } catch (err) {
      const e = err as { code?: string; message?: string };
      const code = e.code ?? '';
      const msg = e.message ?? '';

      if (code.includes('permission-denied')) {
        setError('That PIN did not match. Try again.');
      } else if (code.includes('resource-exhausted')) {
        // The server sends the exact expiry in details (and in the message
        // as a fallback). A real clock time beats "10 minutes", which is
        // already wrong a minute after it's shown.
        const details = (e as { details?: { untilISO?: string } }).details;
        const iso = details?.untilISO ?? msg.match(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/)?.[0];
        const until = iso ? new Date(iso) : null;
        setError(
          until && !Number.isNaN(until.getTime())
            ? `Too many attempts. Try again at ${until.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}.`
            : 'Too many attempts. Try again in 10 minutes.'
        );
      } else if (code.includes('failed-precondition')) {
        // No PIN on file — usually because Kenny reset it. Sending them back
        // to the grid re-runs needsPinSetup on the next tap, which routes
        // them into setup instead of asking for a PIN that doesn't exist.
        setError('Your PIN was reset. Tap your photo again to set a new one.');
        reset();
      } else if (code.includes('already-exists')) {
        setError('PIN already set. Ask Kenny to reset it.');
      } else if (code.includes('invalid-argument')) {
        setError('Check your PIN and try again.');
        console.warn('setBarberPin validation error:', msg);
      } else {
        setError('Something went wrong. Try again.');
        console.error('kiosk submit error:', e);
      }
      setPin('');
      if (mode === 'setup') setConfirmPin('');
    } finally {
      setBusy(false);
    }
  };

  const owner = barbers.find((b) => b.is_owner);
  // The owner is not a valid target: resetting his own PIN through a screen
  // that asks for that same PIN is circular. That case runs from the admin
  // workflow instead.
  const resettable = barbers.filter((b) => !b.is_owner);
  /**
   * Calls the existing owner-gated resetBarberPin. No backend change: the
   * server re-verifies the owner PIN and writes the shopEvents audit row,
   * so this screen cannot grant itself anything the callable would refuse.
   */
  const doOwnerReset = async () => {
    if (!resetTarget) return;
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { barberSlug: string; ownerPin: string },
        { reset: boolean; barberSlug: string; at: number }
      >(getFns(), 'resetBarberPin');
      await fn({ barberSlug: resetTarget, ownerPin: pin });
      setPin('');
      setMode('reset-done');
    } catch (err) {
      const e = err as { code?: string };
      const code = e.code ?? '';
      setError(
        code.includes('permission-denied')
          ? 'That PIN did not match. Start again.'
          : code.includes('resource-exhausted')
            ? 'Too many attempts. Try again in a few minutes.'
            : 'Could not reset that PIN. Try again.'
      );
      // Never leave a failed owner PIN on screen for the next person.
      setPin('');
      setMode('reset-auth');
      console.error('resetBarberPin failed:', err);
    } finally {
      setBusy(false);
    }
  };

  const chosen = selected ? barbers.find((b) => b.slug === selected) : undefined;
  const target = resetTarget ? barbers.find((b) => b.slug === resetTarget) : undefined;

  // Shared with the kiosk: gold ring, cover-fit, same diameter. Two screens
  // showing the same faces should show them the same way.
  const ring = 'rounded-full border-2 border-gold object-cover';
  const pillGold =
    'min-h-[64px] rounded-pill bg-gold px-10 py-4 font-newsreader text-[22px] text-navy disabled:opacity-50';
  const pillOutline =
    'min-h-[64px] rounded-pill border-2 border-paper/40 px-10 py-4 font-newsreader text-[22px] text-paper';
  const label = 'font-oswald text-[18px] font-medium uppercase tracking-[0.18em]';

  /** Pad writes into whichever field is still being filled. */
  const press = (d: string) => {
    if (mode === 'setup' && pin.length >= pinLength) {
      setConfirmPin((v) => (v + d).slice(0, pinLength));
    } else {
      setPin((v) => (v + d).slice(0, pinLength));
    }
  };
  const backspace = () => {
    if (mode === 'setup' && pin.length >= pinLength && confirmPin.length > 0) {
      setConfirmPin((v) => v.slice(0, -1));
    } else {
      setPin((v) => v.slice(0, -1));
    }
  };

  const dots = (value: string, active: boolean) => (
    <span
      className={`flex min-h-[72px] items-center justify-center gap-4 rounded-card border-2 px-6 ${
        active ? 'border-gold bg-navy/60' : 'border-paper/20 bg-navy/30'
      }`}
    >
      {Array.from({ length: pinLength }, (_, i) => (
        <span
          key={i}
          className={`h-4 w-4 rounded-full ${i < value.length ? 'bg-gold' : 'bg-paper/25'}`}
        />
      ))}
    </span>
  );

  return (
    <div className="kd-kiosk-root bg-navy px-8 py-8 text-paper md:px-12 md:py-10">
      <header className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img
            src="/assets/logo/kd-crest.png"
            alt=""
            role="presentation"
            className="h-14 w-14 object-contain"
          />
          <h1 className="font-newsreader text-[40px] font-medium leading-tight text-paper">
            On the clock
          </h1>
        </div>

        <div className="flex flex-wrap gap-3">
          {inOrder.map(([slug, b]) => {
            const barber = barbers.find((x) => x.slug === slug);
            return (
              <span
                key={slug}
                className="flex items-center gap-3 rounded-pill border border-paper/20 px-4 py-2"
              >
                <span className="grid h-7 w-7 place-items-center rounded-full bg-gold font-oswald text-[18px] text-navy">
                  {b.position}
                </span>
                <span className="font-newsreader text-[20px] text-paper">
                  {barber?.display_name ?? slug}
                </span>
                {b.currentStatus === 'cutting' && (
                  <span className={`${label} text-gold`}>cutting</span>
                )}
              </span>
            );
          })}
          {inOrder.length === 0 && (
            <span className="font-newsreader text-[20px] text-paper/60">Nobody on the clock</span>
          )}
        </div>
      </header>

      {/* ── Barber select. Everyone appears, owner included — this is the
             clock, not the walk-in picker, and Kenny clocks in like anyone. */}
      {mode === 'idle' && (
        <div className="mt-14 grid gap-5 sm:grid-cols-3 lg:grid-cols-5">
          {barbers.map((b) => (
            <button
              key={b.slug}
              type="button"
              onClick={async () => {
                setSelected(b.slug);
                const fn = httpsCallable<{ barberSlug: string }, { needsSetup: boolean }>(
                  getFns(),
                  'needsPinSetup'
                );
                try {
                  const res = await fn({ barberSlug: b.slug });
                  setMode(res.data.needsSetup ? 'setup' : 'pin');
                } catch {
                  setMode('pin');
                }
              }}
              className={`flex min-h-[260px] flex-col items-center justify-center rounded-card border-2 p-6 text-center transition ${
                isOn(b.slug) ? 'border-gold bg-navy/60' : 'border-paper/20 bg-navy/30'
              }`}
            >
              <img src={b.portrait.kiosk} alt="" className={`h-28 w-28 ${ring}`} />
              <p className="mt-4 font-newsreader text-[24px] font-medium text-paper">
                {b.display_name}
              </p>
              <p className={`mt-1 ${label} text-gold`}>
                {isOn(b.slug) ? 'Tap to clock out' : 'Tap to clock in'}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* ── PIN entry / first-time setup ─────────────────────────────── */}
      {/* Owner-only. Appears after the owner has authenticated this session;
          the PIN prompt below is the real gate. */}
      {mode === 'idle' && ownerAuthed && owner && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setMode('reset-pick')}
            className={`min-h-[64px] rounded-pill border-2 border-gold px-10 py-4 font-newsreader text-[20px] text-gold`}
          >
            Reset a barber&apos;s PIN
          </button>
        </div>
      )}

      {/* ── Owner: pick whose PIN to reset ───────────────────────────── */}
      {mode === 'reset-pick' && (
        <div className="mx-auto mt-12 max-w-3xl text-center">
          <p className="font-newsreader text-[30px] text-paper">Reset a barber&apos;s PIN</p>
          <p className={`mt-2 ${label} text-paper/55`}>Whose PIN needs resetting?</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {resettable.map((b) => (
              <button
                key={b.slug}
                type="button"
                onClick={() => {
                  setResetTarget(b.slug);
                  setPin('');
                  setError(null);
                  setMode('reset-auth');
                }}
                className="flex min-h-[220px] flex-col items-center justify-center rounded-card border-2 border-paper/20 bg-navy/30 p-5 text-center active:border-gold"
              >
                <img src={b.portrait.kiosk} alt="" className={`h-24 w-24 ${ring}`} />
                <p className="mt-4 font-newsreader text-[22px] font-medium text-paper">
                  {b.display_name}
                </p>
              </button>
            ))}
          </div>
          <button type="button" onClick={reset} className={`mt-10 ${pillOutline}`}>
            Cancel
          </button>
        </div>
      )}

      {/* ── Owner: confirm, in words, before anything is cleared ─────── */}
      {mode === 'reset-confirm' && target && (
        <div className="mx-auto mt-16 max-w-xl text-center">
          <img src={target.portrait.kiosk} alt="" className={`mx-auto h-28 w-28 ${ring}`} />
          <p className="mt-6 font-newsreader text-[34px] leading-tight text-paper">
            Reset {target.display_name}&apos;s PIN?
          </p>
          <p className="mt-3 font-newsreader text-[22px] text-paper/75">
            {target.display_name} will set a new one next time he clocks in.
          </p>
          {error && (
            <p
              role="alert"
              className="mt-6 rounded-card border-2 border-red-accent bg-red-accent/10 px-5 py-4 font-newsreader text-[20px] text-paper"
            >
              {error}
            </p>
          )}
          <div className="mt-10 flex justify-center gap-4">
            <button type="button" onClick={reset} className={pillOutline}>
              Cancel
            </button>
            <button type="button" onClick={doOwnerReset} disabled={busy} className={pillGold}>
              {busy ? '…' : 'Reset PIN'}
            </button>
          </div>
        </div>
      )}

      {/* ── Owner: done ──────────────────────────────────────────────── */}
      {mode === 'reset-done' && target && (
        <div className="mx-auto mt-16 max-w-xl text-center">
          <img src={target.portrait.kiosk} alt="" className={`mx-auto h-28 w-28 ${ring}`} />
          <p className="mt-6 font-newsreader text-[40px] leading-tight text-paper">
            {target.display_name}&apos;s PIN is cleared.
          </p>
          <p className="mt-3 font-newsreader text-[22px] text-paper/75">
            He&apos;ll be asked to set a new one the next time he taps his photo.
          </p>
          <button type="button" onClick={reset} className={`mt-10 ${pillGold}`}>
            Done
          </button>
        </div>
      )}

      {/* Two columns on a landscape iPad: stacked, the keypad's bottom row
          fell below 768px and you had to scroll to reach 0. Portrait has the
          height, so it stacks there. */}
      {(mode === 'pin' || mode === 'setup' || mode === 'reset-auth') && (
        <div className="mx-auto mt-8 grid max-w-3xl items-center gap-8 text-center lg:grid-cols-2 lg:gap-12 lg:text-left">
        <div>
          {(mode === 'reset-auth' ? owner : chosen) && (
            <img
              src={(mode === 'reset-auth' ? owner : chosen)!.portrait.kiosk}
              alt=""
              className={`mx-auto h-24 w-24 lg:mx-0 ${ring}`}
            />
          )}
          <p className="mt-5 font-newsreader text-[30px] text-paper">
            {mode === 'reset-auth'
              ? 'Enter your PIN'
              : mode === 'setup'
                ? 'Set your PIN'
                : selected && isOn(selected)
                  ? 'Clock out'
                  : 'Clock in'}
          </p>
          <p className="mt-1 font-newsreader text-[20px] text-paper/75">
            {mode === 'reset-auth'
              ? `${owner?.display_name} — to reset ${target?.display_name}'s PIN`
              : chosen?.display_name}
          </p>

          {/* Real inputs kept behind the dots: a hardware keyboard still
              works, and the aria-labels stay where a screen reader expects
              them. The pad below writes into the same state. */}
          <div className="mt-8 flex flex-col gap-3">
            <label className="sr-only" htmlFor="kd-pin">
              PIN
            </label>
            <input
              id="kd-pin"
              inputMode="numeric"
              autoFocus
              maxLength={pinLength}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="sr-only"
              aria-label="PIN"
            />
            {dots(pin, mode !== 'setup' || pin.length < pinLength)}

            {mode === 'setup' && (
              <>
                <input
                  inputMode="numeric"
                  maxLength={pinLength}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  className="sr-only"
                  aria-label="Confirm PIN"
                />
                <span className={`${label} text-paper/55`}>Confirm</span>
                {dots(confirmPin, pin.length >= pinLength)}
              </>
            )}
          </div>

        </div>

        <div>
          <div className="grid grid-cols-3 gap-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => press(d)}
                className="min-h-[64px] rounded-card border-2 border-paper/20 bg-navy/40 font-newsreader text-[30px] text-paper active:border-gold"
              >
                {d}
              </button>
            ))}
            <button
              type="button"
              onClick={backspace}
              aria-label="Delete"
              className="min-h-[64px] rounded-card border-2 border-paper/20 bg-navy/40 font-newsreader text-[26px] text-paper active:border-gold"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => press('0')}
              className="min-h-[64px] rounded-card border-2 border-paper/20 bg-navy/40 font-newsreader text-[30px] text-paper active:border-gold"
            >
              0
            </button>
            <span aria-hidden />
          </div>

        </div>

        <div className="lg:col-span-2">
          {mode === 'setup' && confirmPin.length > 0 && pin !== confirmPin && (
            <p className={`mt-5 ${label} text-paper/60`}>PINs don&apos;t match yet</p>
          )}

          {error && (
            <p
              role="alert"
              className="mt-6 rounded-card border-2 border-red-accent bg-red-accent/10 px-5 py-4 font-newsreader text-[20px] text-paper"
            >
              {error}
            </p>
          )}
          {info && <p className="mt-4 font-newsreader text-[20px] text-paper/75">{info}</p>}

          <div className="mt-8 flex gap-4">
            <button type="button" onClick={reset} className={`flex-1 ${pillOutline}`}>
              Cancel
            </button>
            <button
              type="button"
              onClick={mode === 'reset-auth' ? () => setMode('reset-confirm') : submit}
              disabled={!canSubmit}
              className={`flex-1 ${pillGold}`}
            >
              {busy ? '…' : 'Confirm'}
            </button>
          </div>
        </div>
        </div>
      )}

      {/* ── Clocked in / out confirmation, shaped like the kiosk's ───── */}
      {mode === 'done' && selected && (
        <div className="mx-auto mt-16 max-w-lg text-center">
          {chosen && (
            <img src={chosen.portrait.kiosk} alt="" className={`mx-auto h-32 w-32 ${ring}`} />
          )}
          <p className="mt-6 font-newsreader text-[48px] font-medium leading-tight text-paper">
            {doneAction === 'in' ? 'Clocked in.' : 'Clocked out.'}
          </p>
          <p className="mt-3 font-newsreader text-[24px] text-paper/75">
            {chosen?.display_name}
            {doneAction === 'in' ? ' — you are on the floor.' : ' — have a good one.'}
          </p>
          <button type="button" onClick={reset} className={`mt-10 ${pillGold}`}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
